import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ChevronRightIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useInvoices } from '@/hooks/useInvoices'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { invoiceStatusI18nKey, invoiceStatusBadgeClass } from '@/lib/invoiceStatus'

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(amount)
}

export default function InvoicesPage() {
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const { formatMonthYear } = useDateFormatter()
  const { data, isLoading, error } = useInvoices()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      </div>
    )
  }

  const invoices = data?.data ?? []

  return (
    <div className="p-4 space-y-3">
      {invoices.length === 0 ? (
        <div className="text-center py-12 text-base-content/50">
          <div className="flex justify-center mb-3">
            <div className="icon-container">
              <DocumentTextIcon className="w-5 h-5" />
            </div>
          </div>
          <p>{t('no_invoices')}</p>
        </div>
      ) : (
        invoices.map((invoice) => (
          <Link
            key={invoice.id}
            to={`/invoices/${invoice.id}`}
            className="flat-card block"
          >
            <div className="list-item-row">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm">{t('invoice_number', { number: invoice.number })}</p>
                  <span className={invoiceStatusBadgeClass(invoice.status)}>
                    {t(invoiceStatusI18nKey(invoice.status))}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-base-content/60">{formatMonthYear(invoice.billed_at)}</p>
                  <span className="font-bold text-base">฿{formatCurrency(invoice.total)}</span>
                </div>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-base-content/40 shrink-0" />
            </div>
          </Link>
        ))
      )}
    </div>
  )
}
